import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

export interface RevenueStageSummary {
  TEC: number;
  TNC: number;
  TRIAL: number;
  PROTOTYPE: number;
  [key: string]: number;
}

export interface RevenueCardConfig {
  stage: 'TEC' | 'TNC' | 'TRIAL' | 'PROTOTYPE';
  label: string;
  description?: string;
  icon: string;
  color: string;
  bgColor: string;
  route: string;
  queryParams?: any;
}

@Component({
  selector: 'app-stage-cards',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stage-cards.component.html',
  styleUrls: ['./stage-cards.component.scss']
})
export class StageCardsComponent implements OnInit, OnChanges {
  @Input() stageData!: RevenueStageSummary;
  @Input() loading: boolean = false;
  @Input() showCountAnimation: boolean = true;
  @Input() showActionButton: boolean = true;
  @Input() customCardConfigs?: RevenueCardConfig[];
  @Input() baseRoute: string = '/revenue/projects';
  
  @Output() cardClicked = new EventEmitter<string>();
  @Output() viewDetailsClicked = new EventEmitter<string>();
  
  stageCards: any[] = [];
  animatedCounts: { [key: string]: number } = {};
  
  private defaultCardConfigs: RevenueCardConfig[] = [
    {
      stage: 'TEC',
      label: 'TEC Completed',
      icon: 'pi pi-check-circle',
      color: '#10B981',
      bgColor: '#D1FAE5',
      route: this.baseRoute,
      queryParams: { stage: 'TEC' }
    },
    {
      stage: 'TNC',
      label: 'TNC Completed',
      icon: 'pi pi-check-circle',
      color: '#3B82F6',
      bgColor: '#DBEAFE',
      route: this.baseRoute,
      queryParams: { stage: 'TNC' }
    },
    {
      stage: 'TRIAL',
      label: 'Trials Stage',
      description: 'Pending / Active',
      icon: 'pi pi-clock',
      color: '#F59E0B',
      bgColor: '#FEF3C7',
      route: this.baseRoute,
      queryParams: { stage: 'TRIAL' }
    },
    {
      stage: 'PROTOTYPE',
      label: 'Prototype Stage',
      icon: 'pi pi-box',
      color: '#8B5CF6',
      bgColor: '#E9D5FF',
      route: this.baseRoute,
      queryParams: { stage: 'PROTOTYPE' }
    }
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.initializeCards();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stageData'] || changes['customCardConfigs'] || changes['baseRoute']) {
      this.initializeCards();
    }
  }

  private initializeCards(): void {
    const configs = this.customCardConfigs || this.defaultCardConfigs;
    
    // Update routes if baseRoute changed
    if (this.baseRoute !== '/revenue/projects') {
      configs.forEach(config => {
        config.route = this.baseRoute;
      });
    }
    
    this.stageCards = configs.map(config => ({
      ...config,
      count: this.stageData?.[config.stage] || 0,
      animatedCount: 0
    }));
    
    if (this.showCountAnimation) {
      this.startCountAnimations();
    }
  }

  private startCountAnimations(): void {
    if (!this.stageData) return;

    this.stageCards.forEach((card, index) => {
      setTimeout(() => {
        this.animateCount(card.stage, card.count);
      }, index * 150);
    });
  }

  private animateCount(stage: string, target: number): void {
    let current = 0;
    const duration = 800;
    const steps = 20;
    const increment = Math.ceil(target / steps);
    
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(interval);
      }
      this.animatedCounts[stage] = current;
    }, duration / steps);
  }

  getDisplayCount(stage: string): number {
    if (this.showCountAnimation && this.animatedCounts[stage] !== undefined) {
      return this.animatedCounts[stage];
    }
    return this.stageData?.[stage] || 0;
  }

  onCardClick(stage: string): void {
    this.cardClicked.emit(stage);
    
    const card = this.stageCards.find(c => c.stage === stage);
    if (card) {
      this.router.navigate([card.route], {
        queryParams: card.queryParams
      });
    }
  }

  onViewDetails(event: Event, stage: string): void {
    event.stopPropagation();
    this.viewDetailsClicked.emit(stage);
    
    const card = this.stageCards.find(c => c.stage === stage);
    if (card) {
      this.router.navigate([card.route], {
        queryParams: card.queryParams
      });
    }
  }
}